import { Button, Menu } from "@chakra-ui/react"
import { ChevronDownIcon, ChevronRightIcon } from "@pathwright/pathicons"
import { LightMode } from "@pathwright/ux/src/components/chakra/ui/color-mode"
import Link from "@pathwright/ux/src/components/link/Link"
import Pathicon from "@pathwright/ux/src/components/pathicon/Pathicon"
import { Link as ReactRouterLink } from "react-router-dom"
import type { RegistrationAccessRow } from "../registration/access"
import type { OfferButtonRenderProps } from "./CoverPageOffer"
import { useCoverPageTranslate } from "./utils"
import { useCoverPageHasLoadedResource } from "./useCoverPageHasLoadedResource"

function stopCardNavigation(e: React.MouseEvent) {
  // Prevent the card wrapper from opening the offer immersive route.
  e.stopPropagation()
}

function ContinueLabel({ multiple }: { multiple: boolean }) {
  const { t } = useCoverPageTranslate()
  return (
    <>
      {t("offer.continue", { defaultValue: "Continue" })}
      {multiple ? <ChevronDownIcon /> : <ChevronRightIcon />}
    </>
  )
}

type CoverPageContinueButtonProps = {
  accesses: RegistrationAccessRow[]
  /** When true, show the secondary join/create menu item. */
  canCreateAnother: boolean
  /**
   * Menu label for the secondary action (e.g. "Join another…", "Create one…").
   * Callers choose copy from Available Offers + user history.
   */
  anotherActionLabel?: string
  /** Create-another action when not using relativeTo (e.g. Path licensing, scroll). */
  onCreateAnother?: (e: React.MouseEvent) => void
  /** Create-another navigation into the Offer conversion panel. */
  createAnotherRelativeTo?: string
  onCreateAnotherIntent?: () => void
  createAnotherLoading?: boolean
  buttonProps: OfferButtonRenderProps
  renderButton?: (props: OfferButtonRenderProps) => React.ReactNode
}

/**
 * Continue CTA for signed-in users who already have RegistrationAccess on this
 * Cover Page resource (Join Cohort and/or Private Cohort).
 */
export function CoverPageContinueButton({
  accesses,
  canCreateAnother,
  anotherActionLabel,
  onCreateAnother,
  createAnotherRelativeTo,
  onCreateAnotherIntent,
  createAnotherLoading,
  buttonProps,
  renderButton
}: CoverPageContinueButtonProps) {
  const { t } = useCoverPageTranslate()
  const hasLoadedResource = useCoverPageHasLoadedResource()
  const multiple = accesses.length > 1
  const menuAnotherLabel =
    anotherActionLabel ||
    t("offer.create_another_ellipsis", {
      defaultValue: "Create another…"
    })

  if (!multiple) {
    const destination = accesses[0]!.destination
    const continueProps: OfferButtonRenderProps = {
      ...buttonProps,
      children: <ContinueLabel multiple={false} />,
      as: ReactRouterLink,
      to: destination,
      relativeTo: undefined,
      href: undefined,
      // Never inherit convert-CTA `disabled` (e.g. Offer.access Unavailable).
      // subscription_expired stays resumable — destination runs the Home expired flow.
      disabled: false,
      onClick: stopCardNavigation
    }

    return renderButton ? (
      renderButton(continueProps)
    ) : (
      <Button {...continueProps} />
    )
  }

  const triggerProps: OfferButtonRenderProps = {
    ...buttonProps,
    children: <ContinueLabel multiple />,
    to: undefined,
    as: undefined,
    relativeTo: undefined,
    href: undefined,
    disabled: false,
    onClick: stopCardNavigation
  }

  const trigger = renderButton ? (
    renderButton(triggerProps)
  ) : (
    <Button {...triggerProps} />
  )

  return (
    <Menu.Root
      positioning={{
        placement: "bottom",
        strategy: "fixed",
        hideWhenDetached: true
      }}
    >
      <Menu.Trigger asChild>{trigger}</Menu.Trigger>
      {/* Keep the menu inside the Cover dialog's focus and accessibility scope. */}
      <LightMode>
        <Menu.Positioner>
          <Menu.Content
            minW="220px"
            borderRadius="15px"
            onClick={stopCardNavigation}
          >
            {accesses.map((access) => (
              <Menu.Item
                key={access.id}
                value={String(access.cohort_id)}
                asChild
              >
                <Link to={access.destination} textDecor="none">
                  <Pathicon icon="group" />
                  {access.cohort_name}
                </Link>
              </Menu.Item>
            ))}
            {canCreateAnother && createAnotherRelativeTo && (
              <>
                <Menu.Separator />
                <Menu.Item
                  value="create-another"
                  disabled={!hasLoadedResource}
                  asChild={hasLoadedResource}
                >
                  {hasLoadedResource ? (
                    <Link
                      relativeTo={createAnotherRelativeTo}
                      textDecor="none"
                      onPointerDown={onCreateAnotherIntent}
                      onClick={(e: React.MouseEvent) => {
                        stopCardNavigation(e)
                        onCreateAnotherIntent?.()
                      }}
                    >
                      {menuAnotherLabel}
                    </Link>
                  ) : (
                    menuAnotherLabel
                  )}
                </Menu.Item>
              </>
            )}
            {canCreateAnother &&
              !createAnotherRelativeTo &&
              onCreateAnother && (
                <>
                  <Menu.Separator />
                  <Menu.Item
                    value="create-another"
                    disabled={!hasLoadedResource || createAnotherLoading}
                    onClick={hasLoadedResource ? onCreateAnother : undefined}
                  >
                    <Pathicon icon="plus" />
                    {menuAnotherLabel}
                  </Menu.Item>
                </>
              )}
          </Menu.Content>
        </Menu.Positioner>
      </LightMode>
    </Menu.Root>
  )
}

export default CoverPageContinueButton
